import { Merge } from 'type-fest'

import {
  PAYMENT_CONTACT_FIELD_ID,
  PAYMENT_PRODUCT_FIELD_ID,
  PAYMENT_VARIABLE_INPUT_AMOUNT_FIELD_ID,
} from '~shared/constants'
import {
  ProductItem,
  SingleAnswerResponse,
  VerifiableResponseBase,
} from '~shared/types'
import {
  AttachmentFieldBase,
  BasicField,
  BotMDDropdownFieldBase,
  CheckboxFieldBase,
  DateFieldBase,
  DecimalFieldBase,
  DropdownFieldBase,
  EmailFieldBase,
  FormFieldDto,
  FormFieldWithId,
  HomenoFieldBase,
  ImageFieldBase,
  LongTextFieldBase,
  MobileFieldBase,
  MyInfoChildAttributes,
  NricFieldBase,
  NumberFieldBase,
  RadioFieldBase,
  RatingFieldBase,
  ReminderFieldBase,
  SectionFieldBase,
  ShortTextFieldBase,
  StatementFieldBase,
  TableFieldBase,
  UenFieldBase,
  YesNoFieldBase,
} from '~shared/types/field'
import { ChildrenCompoundFieldBase } from '~shared/types/field/childrenCompoundField'

import { FormFieldWithQuestionNo } from '~features/form/types'

// Form field types to be composed by various fields
export type BaseFieldOutput<FF extends FormFieldDto> = {
  _id: FF['_id']
  fieldType: FF['fieldType']
  question: FF['title']
}

// Inputs to pass to react-hook-form for better type checking
export type FieldInput<Input> = {
  [schemaId: string]: Input
}
// Type for react-hook-form's FieldValues generic.
export type FormFieldValues = Record<
  FormFieldDto['_id'],
  FormFieldValue<FormFieldDto['fieldType']>
> & {
  [PAYMENT_CONTACT_FIELD_ID]?: { value: string }
  [PAYMENT_VARIABLE_INPUT_AMOUNT_FIELD_ID]?: string
  [PAYMENT_PRODUCT_FIELD_ID]?: Array<ProductItem>
}

export type AttachmentFieldInput = FieldInput<File>
export type CheckboxFieldInputs = FieldInput<CheckboxFieldValues>
export type RadioFieldInputs = FieldInput<RadioFieldValues>
export type TableFieldInputs = FieldInput<TableFieldValues>
export type YesNoFieldInput = FieldInput<YesNoFieldValue>
export type SingleAnswerFieldInput = FieldInput<SingleAnswerValue>
export type VerifiableFieldInput = FieldInput<VerifiableFieldValues>
export type ChildrenCompoundFieldInputs =
  FieldInput<ChildrenCompoundFieldValues>
export type ReminderFieldInput = FieldInput<ReminderFieldValue>

export type FormFieldValue<F extends BasicField = BasicField> = F extends
  | BasicField.Number
  | BasicField.Decimal
  | BasicField.ShortText
  | BasicField.LongText
  | BasicField.HomeNo
  | BasicField.Dropdown
  | BasicField.BotMDDropdown
  | BasicField.Rating
  | BasicField.Nric
  | BasicField.Uen
  | BasicField.Date
  | BasicField.CountryRegion
  ? SingleAnswerValue
  : F extends BasicField.YesNo
  ? YesNoFieldValue
  : F extends BasicField.Attachment
  ? File
  : F extends BasicField.Email | BasicField.Mobile
  ? VerifiableFieldValues
  : F extends BasicField.Table
  ? TableFieldValues
  : F extends BasicField.Radio
  ? RadioFieldValues
  : F extends BasicField.Checkbox
  ? CheckboxFieldValues
  : F extends BasicField.Reminder
  ? ReminderFieldValue
  : F extends BasicField.Children
  ? ChildrenCompoundFieldValues
  : never

// Input values, what each field contains
export type SingleAnswerValue = string
export type MultiAnswerValue = string[]
export type YesNoFieldValue = string
export type VerifiableFieldValues = {
  signature?: string
  value: string
}
export type CheckboxFieldValues = {
  // Can be `false` if no changes were triggered on checkbox field.
  // Artifact of react-hook-form not knowing whether checkbox is an array or not.
  // Unable to use `{schema}.value.{index}` since value is a nested field value.
  value: string[] | false
  othersInput?: string
  score: number
}
export type RadioFieldValues = {
  value: string
  othersInput?: string
  score: number
}

export enum ReminderType {
  OneTime = 'one-time',
  Recurring = 'recurring',
}

export enum ReminderEventType {
  Enrollment = 'enrollment',
  Exact = 'exact',
  Never = 'never',
  Discharge = 'discharge',
  ProgramEnrollment = 'program_enrollment',
  ProgramDischarge = 'program_discharge',
}

export enum ReminderUnitType {
  Day = 'day',
  Week = 'week',
  Month = 'month',
}

export enum ReminderDayOfWeekType {
  Sun = 'sun',
  Mon = 'mon',
  Tue = 'tue',
  Wed = 'wed',
  Thu = 'thu',
  Fri = 'fri',
  Sat = 'sat',
}

export enum ReminderActionType {
  Discharge = 'discharge',
  SendTemplate = 'send_template',
  SendReminder = 'send_reminder',
}

export type ReminderSendTemplate = {
  purpose: string
  template_set?: string
}

export type ReminderAction = {
  type: ReminderActionType
  send_template?: ReminderSendTemplate
  send_reminder?: JSON
  discharge?: JSON
}

export type ReminderFrequencyFieldValue = {
  unit?: ReminderUnitType
  duration?: number
  time_of_day?: string
  day_of_week?: ReminderDayOfWeekType
  day_of_month?: number
}

export type ReminderTriggerFieldValues = {
  duration?: number
  time_of_day?: string
  unit?: ReminderUnitType
  event?: ReminderEventType
  require_enrollment?: boolean
  exact_trigger_on?: string
}

export type ReminderScheduleFieldValues = {
  action: ReminderAction
  recurring_frequencies?: ReminderFrequencyFieldValue[]
  start_trigger?: ReminderTriggerFieldValues
  end_trigger?: ReminderTriggerFieldValues
}

export type ReminderFieldValue = {
  schedule: ReminderScheduleFieldValues
}

export type TableRowFieldValue = {
  [columnId: string]: string
}
export type TableFieldValues = TableRowFieldValue[]

export type SingleAnswerOutput<F extends FormFieldDto> =
  SingleAnswerResponse & {
    fieldType: F['fieldType']
  }

export type SingleAnswerWithScoreOutput<F extends FormFieldDto> =
  SingleAnswerResponse & {
    fieldType: F['fieldType']
    score: number
  }

export type VerifiableAnswerOutput<F extends FormFieldDto> = Merge<
  SingleAnswerOutput<F>,
  VerifiableResponseBase
>

export type ChildrenCompoundFieldValues = {
  // Each subarray represents one child value
  // e.g.
  // child : [['NAME', 'T12345678Z', 'VACCINATED'], ... ]
  // childFields: [name, bc number, vaxx status]
  child: string[][]
  // Array of attribute names
  childFields: MyInfoChildAttributes[]
}

// Various schemas used by different fields
export type SectionFieldSchema = FormFieldWithId<SectionFieldBase>
export type ParagraphFieldSchema = FormFieldWithId<StatementFieldBase>
export type ImageFieldSchema = FormFieldWithId<ImageFieldBase>

// With question number
export type AttachmentFieldSchema = FormFieldWithQuestionNo<AttachmentFieldBase>
export type CheckboxFieldSchema = FormFieldWithQuestionNo<CheckboxFieldBase>
export type DateFieldSchema = FormFieldWithQuestionNo<DateFieldBase>
export type DecimalFieldSchema = FormFieldWithQuestionNo<DecimalFieldBase>
export type DropdownFieldSchema = FormFieldWithQuestionNo<DropdownFieldBase>
export type BotMDDropdownFieldSchema =
  FormFieldWithQuestionNo<BotMDDropdownFieldBase>
export type EmailFieldSchema = FormFieldWithQuestionNo<EmailFieldBase>
export type HomeNoFieldSchema = FormFieldWithQuestionNo<HomenoFieldBase>
export type LongTextFieldSchema = FormFieldWithQuestionNo<LongTextFieldBase>
export type MobileFieldSchema = FormFieldWithQuestionNo<MobileFieldBase>
export type NricFieldSchema = FormFieldWithQuestionNo<NricFieldBase>
export type NumberFieldSchema = FormFieldWithQuestionNo<NumberFieldBase>
export type RadioFieldSchema = FormFieldWithQuestionNo<RadioFieldBase>
export type RatingFieldSchema = FormFieldWithQuestionNo<RatingFieldBase>
export type ShortTextFieldSchema = FormFieldWithQuestionNo<ShortTextFieldBase>
export type TableFieldSchema = FormFieldWithQuestionNo<TableFieldBase>
export type UenFieldSchema = FormFieldWithQuestionNo<UenFieldBase>
export type YesNoFieldSchema = FormFieldWithQuestionNo<YesNoFieldBase>
export type ReminderFieldSchema = FormFieldWithQuestionNo<ReminderFieldBase>
export type ChildrenCompoundFieldSchema =
  FormFieldWithQuestionNo<ChildrenCompoundFieldBase>
